interface RateLimitConfig {
  maxRequests: number;
  windowMs: number;
  message?: string;
}

interface RequestRecord {
  timestamp: number;
  count: number;
}

class RateLimiter {
  private requests = new Map<string, RequestRecord>();
  private config: RateLimitConfig;
  private cleanupIntervalId: ReturnType<typeof setInterval> | null = null;
  private static readonly CLEANUP_INTERVAL_MS = 60000; // 60 seconds

  constructor(config: RateLimitConfig) {
    this.config = {
      message: 'Too many requests. Please try again later.',
      ...config,
    };
    // Cleanup is now done lazily in checkRequest() to avoid background timer overhead.
    // Call startCleanupTimer() explicitly if background cleanup is preferred.
  }

  /**
   * Start the background cleanup timer (opt-in)
   * Call this method if you prefer background cleanup over lazy cleanup.
   */
  startCleanupTimer(): void {
    // Avoid duplicate timers
    if (this.cleanupIntervalId !== null) return;

    this.cleanupIntervalId = setInterval(() => {
      const windowStart = Date.now() - this.config.windowMs;
      this.cleanupOldRecords(windowStart);
    }, RateLimiter.CLEANUP_INTERVAL_MS);
  }

  /**
   * Stop the background cleanup timer (for testing or shutdown)
   */
  stopCleanupTimer(): void {
    if (this.cleanupIntervalId !== null) {
      clearInterval(this.cleanupIntervalId);
      this.cleanupIntervalId = null;
    }
  }

  /**
   * Check if request is allowed within rate limit
   * @param key - Unique identifier for the request (e.g., user ID, IP, endpoint)
   * @returns Object with allowed status and retry info
   */
  checkRequest(key: string): {
    allowed: boolean;
    retryAfter?: number;
    message?: string;
    remainingRequests?: number;
  } {
    const now = Date.now();
    const windowStart = now - this.config.windowMs;

    // Lazy cleanup: remove expired records when checking requests
    this.cleanupOldRecords(windowStart);

    const record = this.requests.get(key);
    
    if (!record) {
      // First request
      this.requests.set(key, { timestamp: now, count: 1 });
      return {
        allowed: true,
        remainingRequests: this.config.maxRequests - 1,
      };
    }
    
    if (record.timestamp < windowStart) {
      // Reset window
      this.requests.set(key, { timestamp: now, count: 1 });
      return {
        allowed: true,
        remainingRequests: this.config.maxRequests - 1,
      };
    }
    
    if (record.count >= this.config.maxRequests) {
      // Rate limit exceeded
      const retryAfter = Math.ceil((record.timestamp + this.config.windowMs - now) / 1000);
      return {
        allowed: false,
        retryAfter,
        message: this.config.message,
        remainingRequests: 0,
      };
    }
    
    // Increment request count
    record.count += 1;
    this.requests.set(key, record);
    
    return {
      allowed: true,
      remainingRequests: this.config.maxRequests - record.count,
    };
  }

  /**
   * Reset rate limit for a specific key
   */
  reset(key: string): void {
    this.requests.delete(key);
  }

  /**
   * Clear all rate limit records
   */
  resetAll(): void {
    this.requests.clear();
  }

  private cleanupOldRecords(windowStart: number): void {
    for (const [key, record] of this.requests.entries()) {
      if (record.timestamp < windowStart) {
        this.requests.delete(key);
      }
    }
  }
}

// Pre-configured rate limiters for different use cases
export const apiRateLimiter = new RateLimiter({
  maxRequests: 100,
  windowMs: 15 * 60 * 1000, // 15 minutes
  message: 'API rate limit exceeded. Please try again in a few minutes.',
});

export const loginRateLimiter = new RateLimiter({
  maxRequests: 5,
  windowMs: 15 * 60 * 1000, // 15 minutes
  message: 'Too many login attempts. Please try again in 15 minutes.',
});

export const searchRateLimiter = new RateLimiter({
  maxRequests: 50,
  windowMs: 60 * 1000, // 1 minute
  message: 'Search rate limit exceeded. Please wait a moment before searching again.',
});

// Exponential backoff utility
export class ExponentialBackoff {
  private attempts = new Map<string, number>();
  private baseDelay: number;
  private maxDelay: number;
  private maxAttempts: number;

  constructor(baseDelay = 1000, maxDelay = 30000, maxAttempts = 5) {
    this.baseDelay = baseDelay;
    this.maxDelay = maxDelay;
    this.maxAttempts = maxAttempts;
  }

  /**
   * Calculate delay for next retry
   * @param key - Unique identifier for the operation
   * @returns Delay in milliseconds, or null if max attempts exceeded
   */
  getDelay(key: string): number | null {
    const attempts = this.attempts.get(key) || 0;
    
    if (attempts >= this.maxAttempts) {
      return null; // Max attempts exceeded
    }
    
    this.attempts.set(key, attempts + 1);
    
    const delay = Math.min(
      this.baseDelay * Math.pow(2, attempts),
      this.maxDelay
    );
    
    // Add jitter to prevent thundering herd
    const jitter = Math.random() * 0.1 * delay;
    return delay + jitter;
  }

  /**
   * Reset attempts counter for a key
   */
  reset(key: string): void {
    this.attempts.delete(key);
  }

  /**
   * Check if operation should be retried
   */
  canRetry(key: string): boolean {
    const attempts = this.attempts.get(key) || 0;
    return attempts < this.maxAttempts;
  }
}

// Pre-configured backoff instances
export const apiBackoff = new ExponentialBackoff(1000, 30000, 3);
export const uploadBackoff = new ExponentialBackoff(2000, 60000, 5);

// Utility function to sleep
export const sleep = (ms: number): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

// Rate-limited fetch wrapper
export async function rateLimitedFetch(
  url: string,
  options?: RequestInit,
  rateLimiter?: RateLimiter,
  backoff?: ExponentialBackoff
): Promise<Response> {
  const limiter = rateLimiter || apiRateLimiter;
  const retryBackoff = backoff || apiBackoff;
  const key = `${options?.method || 'GET'}:${url}`;
  
  // Check rate limit
  const rateLimitCheck = limiter.checkRequest(key);
  if (!rateLimitCheck.allowed) {
    throw new Error(rateLimitCheck.message || 'Rate limit exceeded');
  }
  
  let lastError: Error | null = null;
  
  while (retryBackoff.canRetry(key)) {
    try {
      const response = await fetch(url, options);
      
      if (response.ok) {
        retryBackoff.reset(key);
        return response;
      }
      
      // Handle rate limiting from server
      if (response.status === 429) {
        const retryAfter = response.headers.get('Retry-After');
        const delay = retryAfter ? parseInt(retryAfter) * 1000 : retryBackoff.getDelay(key);
        
        if (delay !== null) {
          await sleep(delay);
          continue;
        }
      }
      
      // Handle server errors with exponential backoff
      if (response.status >= 500) {
        const delay = retryBackoff.getDelay(key);
        if (delay !== null) {
          await sleep(delay);
          continue;
        }
      }
      
      // Don't retry client errors (4xx except 429)
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      
    } catch (error) {
      lastError = error as Error;
      
      // Network errors - retry with exponential backoff
      if (error instanceof TypeError && error.message.includes('fetch')) {
        const delay = retryBackoff.getDelay(key);
        if (delay !== null) {
          await sleep(delay);
          continue;
        }
      }
      
      throw error;
    }
  }
  
  throw lastError || new Error('Max retry attempts exceeded');
}

export { RateLimiter };